/* eslint-disable */
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import worker_script from './timer-worker';

/**
 * Global Timer Component.
 * This is responsible for initiating the
 * timeElpased and totalTime, also every
 * 1 secs the Timer component updates the
 * store so that other TimerCounter component
 * get realtime presentation updates
 */
class Timer extends PureComponent {
  static displayName = 'Timer';

  static propTypes = {
    /* time used by the user */
    timeElapsed: PropTypes.number.isRequired,
    /* total time for the quiz */
    totalTime: PropTypes.number.isRequired,
    /* callback func when the user time has exhausted */
    onTimerEnd: PropTypes.func,
    /*
     * Timer count format.
     * The countFormat helps to show different Timer view
     * during taking a quiz by the student.
     * When countFormat is up then we show the endTime to
     * the student. eg 10:00/20:00, otherwise when countFormat
     * counts down then we dont show the endTime eg 09:34
     */
    countFormat: PropTypes.oneOf(['up', 'down']),
    /* callback function when to get current timer info
     * when the time ticks
     */
    onTimerUpdate: PropTypes.func,
    isSoftTime: PropTypes.bool,
    /* callback that is called to see if the timer has been extended. */
    checkTimeExtension: PropTypes.func,
    /* how frequently (in milliseconds) should we query if the timer has been extended. */
    checkTimeExtensionInterval: PropTypes.number,
    /* callback called just as time as expired.
     * This is a last chance to check if the teacher has extended the time between
     * the last interval.
     * NOTE: If this is defined, then the responsibility for checking to see if the timer
     * belongs to this function. It must be async and return true if the time is expired.
     */
    onTimerExpiring: PropTypes.func,
    /**
     * Timer is running
     * */
    isRunning: PropTypes.bool,
    learnosityData: PropTypes.shape({
      activity: PropTypes.object,
      items: PropTypes.object,
      currentItem: PropTypes.object,
      app: PropTypes.object,
    }),
  };

  static defaultProps = {
    countFormat: 'down',
    isSoftTime: true,
    onTimerUpdate: () => {},
    onTimerEnd: () => {},
    checkTimeExtension: () => {},
    checkTimeExtensionInterval: 30000,
    onTimerExpiring: null,
    timeElapsed: 0,
    totalTime: 0,
    isRunning: false,
  };

  constructor(props) {
    super(props);

    const startFrom = this.timeUsedStartsFrom(props);
    this.state = {
      timeUsed: startFrom,
      lastExtendTimeCheck: 0,
    };

    const workerTimer = new Worker(worker_script);
    this.workerTimer = workerTimer;
    this.timerInterval = null;
  }

  componentDidMount() {
    const { timeUsed } = this.state;
    const { totalTime, timeElapsed } = this.props;
    /**
     * First time the user begins the quiz, timeElapsed is 0
     * - it has to tick - start the counter
     * If the user resumes the quiz
     * - it has to calculate if it should start the counter
     * */
    let canTick = false;
    if (totalTime > 0 && timeElapsed === 0) {
      canTick = true;
    } else if (timeElapsed > totalTime) {
      canTick = false;
    } else {
      canTick = totalTime - timeUsed > 0;
    }
    this.shouldStartTickingAgain(canTick);
  }

  componentWillUnmount() {
    this.workerTimer.postMessage({ turn: 'off' });
  }

  componentWillReceiveProps(nextProps) {
    if (
      nextProps.timeElapsed !== this.props.timeElapsed ||
      nextProps.totalTime !== this.props.totalTime
    ) {
      const startFrom = this.timeUsedStartsFrom(nextProps);
      // set the component internal timeUsed state
      this.setState({
        timeUsed: startFrom,
      });
      const timerUpdate = {
        totalTime: nextProps.totalTime,
        timeElapsed: startFrom,
        showWarning: startFrom < 1 ? false : this.isWarningTime(),
      };
      // notify other component about the updates
      if (nextProps.timeElapsed > nextProps.totalTime) {
        this.workerTimer.postMessage({ turn: 'off' });
      } else {
        this.shouldStartTickingAgain(nextProps.totalTime - startFrom > 0);
        this.props.onTimerUpdate(timerUpdate);
      }
    }
  }

  startTicking = () => {
    this.timerInterval = true;

    this.workerTimer.postMessage({
      turn: 'on',
    });

    this.workerTimer.onmessage = () => {
      this.tickTime();
    };
  };

  /**
   * Check if the timer interval is null. If it is, set timeout has been cleared.
   * If time is extended after the timer has been cleared, we need to make sure it ticks again
   * So, if we have called clearTimeout, but the total time is greater than the time elapsed, we need
   * to setTimeout again.
   */
  shouldStartTickingAgain = (shouldTick) => {
    if (!this.timerInterval && shouldTick) {
      this.startTicking();
    } else {
      // just send updates to other component about the curret state of the timer.
      // Also check to see if the user has loaded the player with an expired timer,
      // because, if hardtime limit is turned on, we need to check and submit.
      if (!this.props.isSoftTime) {
        this.hasTimeElasped();
      }
      const timerUpdate = {
        showWarning: this.isWarningTime(),
      };
      this.props.onTimerUpdate(timerUpdate);
    }
  };

  /*
   * Check if the user remaining time or timeUsed
   * is less or equal to 5 mins
   * */
  isWarningTime = () => {
    const { totalTime, countFormat } = this.props;
    const { timeUsed } = this.state;
    //TODO see if this can be configurable from the backend
    const fiveMins = 5 * 60;
    const diff = countFormat === 'up' ? totalTime - timeUsed : timeUsed;
    return diff <= fiveMins;
  };

  /**
   * Check if the user quiz time
   * has exhausted. Nofify all callbacks
   * about this and stop the Timer from
   * ticking, by clearing the setInterval
   */
  hasTimeElasped = async () => {
    const { totalTime, countFormat, onTimerExpiring } = this.props;
    const { timeUsed } = this.state;
    const timeExhausted =
      countFormat === 'up' ? totalTime === timeUsed : timeUsed <= 0;
    if (timeExhausted) {
      // the time ran out, but let's do one last check to make sure that's true.
      if (onTimerExpiring) {
        const expired = await onTimerExpiring();
        if (expired) {
          this.onTimeExhausted();
        }
      } else {
        this.onTimeExhausted();
      }
    }
  };

  /**
   * Once we're out of time, then we call this.
   * It's also passed to an `onTimerExpiring` callback, wherein the responsibility of checking if
   * time has been exhausted is passed off to that callback.
   */
  onTimeExhausted = () => {
    const { onTimerEnd } = this.props;
    onTimerEnd(true);
    if (this.timerInterval) {
      this.workerTimer.postMessage({ turn: 'off' });
      this.timerInterval = null;
    }
  };

  /**
   * Update the quiz timeUsed by
   * adding 1  if the countFormat is up
   * removing 1 if otherwise
   */
  tickTime = async () => {
    const { timeUsed, lastExtendTimeCheck } = this.state;
    const {
      countFormat,
      timeElapsed,
      totalTime,
      onTimerUpdate,
      checkTimeExtension,
      checkTimeExtensionInterval,
      isSoftTime,
    } = this.props;

    if (checkTimeExtension) {
      // this will happen asynchronously but it's just to allow for checking if the time has been extended;
      const now = new Date().valueOf();
      if (now - lastExtendTimeCheck >= checkTimeExtensionInterval) {
        await checkTimeExtension();
        this.setState({ lastExtendTimeCheck: now });
      }
    }

    // dont tick when timeUsed is greater than totalTime
    if (timeUsed > totalTime || timeElapsed > totalTime || totalTime === 0) {
      if (!isSoftTime) {
        // If hard time limit students leave exam and return once timer has elapsed
        this.hasTimeElasped();
      }
      return;
    }

    const newVal = countFormat === 'up' ? timeUsed + 1 : timeUsed - 1;
    // notify all other component about the new update by calling their callbacks
    onTimerUpdate({
      totalTime,
      timeElapsed: newVal,
      showWarning: this.isWarningTime(),
    });
    // set the component internal timeUsed for calculating the next time
    this.setState({ timeUsed: newVal });
    // check if the time has exahusted
    this.hasTimeElasped();
  };

  /**
   * calculate the time to start from
   * with respect to the countFormat.
   * @params {string} countFormat used for determining the count
   * @params {number} timeElapsed during taking the quiz
   * @params {number} totalTime for the quiz
   */
  timeUsedStartsFrom({ countFormat, timeElapsed, totalTime }) {
    if (totalTime === 0 || timeElapsed > totalTime) {
      return 0;
    }
    return countFormat === 'up' ? timeElapsed : totalTime - timeElapsed;
  }

  render() {
    const { children } = this.props;

    return children;
  }
}

export default Timer;
